import React, { useEffect, useSyncExternalStore } from 'react';
import { Backdrop, Box, CircularProgress, Paper, Typography } from '@mui/material';
import {
  endLoading,
  getLoadingSnapshot,
  startLoading,
  subscribeLoading,
  updateLoading,
} from '../../utils/globalLoading';

/**
 * Keeps the centre-screen overlay up while `active` is true. For work that is
 * not a single network call — e.g. an export assembled in the browser from
 * several paged requests — driven by a component's existing loading state.
 */
export const useGlobalLoadingFlag = (active, message, progress = null) => {
  const idRef = React.useRef(null);

  useEffect(() => {
    if (active && idRef.current === null) idRef.current = startLoading(message, progress);
    if (!active && idRef.current !== null) {
      endLoading(idRef.current);
      idRef.current = null;
    }
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (idRef.current !== null) updateLoading(idRef.current, { message, progress });
  }, [message, progress]);

  useEffect(() => () => {
    if (idRef.current !== null) endLoading(idRef.current);
  }, []);
};

/** Mounted once in App. Blocks the page while any upload/download runs. */
const GlobalLoadingOverlay = () => {
  const task = useSyncExternalStore(subscribeLoading, getLoadingSnapshot);
  const hasProgress = typeof task?.progress === 'number';

  return (
    <Backdrop open={Boolean(task)} sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}>
      <Paper elevation={8} sx={{ px: 4, py: 3, minWidth: 240, maxWidth: 360, textAlign: 'center', borderRadius: 2 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1.5 }}>
          <CircularProgress
            size={56}
            variant={hasProgress ? 'determinate' : 'indeterminate'}
            value={hasProgress ? task.progress : undefined}
          />
          {hasProgress && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" fontWeight={600}>{`${Math.round(task.progress)}%`}</Typography>
            </Box>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">{task?.message}</Typography>
      </Paper>
    </Backdrop>
  );
};

export default GlobalLoadingOverlay;
